import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form as AntForm,
  Input,
  Select,
  Space,
  Spin,
  Switch,
} from 'antd';
import dayjs from 'dayjs';
import { History, Save, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { RichTextEditor } from '@shared/components/RichTextEditor';
import { ImageField } from '@shared/components/MediaUpload';
import { SeoPanel, type SeoPanelValue } from '@shared/components/SeoPanel';
import { KeywordsInput } from '@shared/components/KeywordsInput';
import { CustomCodeField } from '@shared/components/CustomCodeField/CustomCodeField';
import { StatusBadge } from '@shared/components/StatusBadge';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { VariantPanel } from '@shared/components/VariantPanel/VariantPanel';
import { useConfirm, usePrompt } from '@shared/components/ConfirmDialog';
import { useCan } from '@shared/rbac/useCan';
import { isSafeUrl } from '@shared/utils/safeUrl';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { campaignsApi } from '@api/campaigns';
import { categoriesApi } from '@api/categories';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import { ROUTES } from '@shared/constants/routes';
import type { Translator } from '@/i18n/config';
import { CampaignVersionsDrawer } from '../components/CampaignVersionsDrawer';
import {
  toContentStatus,
  useCampaignStatusLabel,
  type Campaign,
  type CampaignStatus,
  type CampaignWriteInput,
} from '../types';

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    title: z.string().min(3, t('form.validation.titleMin')).max(200),
    slug: z.string().min(2).max(200).regex(SLUG_RULE, t('form.validation.slugRegex')),
    body: z.string().refine((v) => v.replace(/<[^>]+>/g, '').trim().length >= 20, t('form.validation.bodyMin')),
    excerpt: z.string().max(500).default(''),
    ctaLink: z
      .string()
      .max(500)
      .refine((v) => isSafeUrl(v), t('validation:safeUrl'))
      .optional()
      .nullable(),
    metaTitle: z.string().max(200).optional().nullable(),
    metaDescription: z.string().max(500).optional().nullable(),
    canonicalTag: z.string().max(500).optional().nullable(),
    customCode: z.string().max(5000).optional().nullable(),
    keywords: z.string().max(500, t('form.validation.keywordsMax')).optional().nullable(),
    changeNote: z.string().max(500).optional().nullable(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

function emptyValues(): Values {
  return { title: '', slug: '', body: '', excerpt: '', ctaLink: null, metaTitle: null, metaDescription: null, canonicalTag: null, customCode: null, keywords: null, changeNote: null };
}

function toValues(c: Campaign): Values {
  return {
    title: c.title,
    slug: c.slug,
    body: c.body,
    excerpt: c.excerpt,
    ctaLink: c.ctaLink,
    metaTitle: c.metaTitle,
    metaDescription: c.metaDescription,
    canonicalTag: c.canonicalTag,
    customCode: c.customCode,
    keywords: c.keywords,
    changeNote: null,
  };
}

export function CampaignEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  const variantGroupId = searchParams.get('groupId') ?? undefined;
  const variantLang = searchParams.get('lang') ?? undefined;
  const { t } = useTranslation(['campaigns', 'common', 'validation', 'errors']);
  const statusLabel = useCampaignStatusLabel();
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const promptDialog = usePrompt();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const canDelete = useCan(PERMISSIONS.CAMPAIGN_DELETE);
  // K-6 — CustomCode alanı yalnız content.customcode yetkisiyle düzenlenebilir.
  const canEditCustomCode = useCan(PERMISSIONS.CONTENT_CUSTOM_CODE);
  const { data: activeLanguages = [] } = useActiveLanguages();

  const [pickedLanguageId, setPickedLanguageId] = useState<string | undefined>(variantLang);
  const languageId = pickedLanguageId ?? pickDefaultLanguage(activeLanguages)?.id;
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [publishDate, setPublishDate] = useState<string | null>(null);
  const [allowIndexing, setAllowIndexing] = useState(true);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [hydratedId, setHydratedId] = useState<string | null>(null);

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => campaignsApi.get(id ?? ''),
    enabled: !isNew,
  });

  // Hydrate the non-form (date/category/cover) state once from the loaded record
  // without an effect (guard by id to run once per campaign).
  if (campaign && hydratedId !== campaign.id) {
    setHydratedId(campaign.id);
    setCategoryId(campaign.categoryId);
    setCoverImageUrl(campaign.coverImageUrl);
    setStartDate(campaign.startDate);
    setEndDate(campaign.endDate);
    setPublishDate(campaign.publishDate);
    setAllowIndexing(campaign.allowIndexing);
  }

  const { data: categoriesData } = useQuery({
    // Kampanya taksonomisi Article kategorilerini reuse eder (Faz 2 Blok D) —
    // key fetch'le aynı scope'u taşımalı ki ArticleEditPage cache'iyle paylaşılsın.
    queryKey: ['categories', { scope: 'ARTICLE' }],
    queryFn: () => categoriesApi.list({ scope: 'ARTICLE' }),
    retry: false,
  });

  const initialValues = useMemo<Values>(() => (campaign ? toValues(campaign) : emptyValues()), [campaign]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['campaigns'] });
    if (id) await qc.invalidateQueries({ queryKey: ['campaigns', id] });
  };

  const createMutation = useMutation({
    mutationFn: (input: CampaignWriteInput) => campaignsApi.create(input),
    onSuccess: async (created) => {
      toast.success(t('toast.created'));
      await invalidate();
      void navigate(ROUTES.CAMPAIGN_EDIT.replace(':id', created.id));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: CampaignWriteInput }) => campaignsApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('toast.saved'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const workflowSuccess = (msg: string, action: string) => async () => {
    toast.success(msg);
    await invalidate();
    void recordAuditAction({ action, resource: 'campaign', resourceId: id, summary: msg });
  };
  const onWorkflowError = (err: unknown) => toast.error(resolveApiErrorMessage(err));

  const submitReviewMutation = useMutation({
    mutationFn: () => campaignsApi.submitReview(id ?? ''),
    onSuccess: workflowSuccess(t('toast.submittedReview'), 'campaign.review.submit'),
    onError: onWorkflowError,
  });
  const approveMutation = useMutation({
    mutationFn: () => campaignsApi.approve(id ?? '', true),
    onSuccess: workflowSuccess(t('toast.approved'), 'campaign.review.approve'),
    onError: onWorkflowError,
  });
  const publishMutation = useMutation({
    mutationFn: () => campaignsApi.publish(id ?? ''),
    onSuccess: workflowSuccess(t('toast.published'), 'campaign.publish'),
    onError: onWorkflowError,
  });
  const archiveMutation = useMutation({
    mutationFn: () => campaignsApi.archive(id ?? ''),
    onSuccess: workflowSuccess(t('toast.archived'), 'campaign.archive'),
    onError: onWorkflowError,
  });
  const rejectMutation = useMutation({
    mutationFn: (note: string) => campaignsApi.approve(id ?? '', false, note),
    onSuccess: workflowSuccess(t('toast.rejected'), 'campaign.review.reject'),
    onError: onWorkflowError,
  });
  const deleteMutation = useMutation({
    mutationFn: () => campaignsApi.remove(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
      void navigate(ROUTES.CAMPAIGNS);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const mutationsBusy =
    submitReviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending;

  const onSubmit = (v: Values) => {
    const base: CampaignWriteInput = {
      title: v.title,
      slug: v.slug,
      body: v.body,
      excerpt: v.excerpt,
      categoryId,
      coverImageUrl,
      startDate,
      endDate,
      ctaLink: v.ctaLink ?? null,
      metaTitle: v.metaTitle ?? null,
      metaDescription: v.metaDescription ?? null,
      canonicalTag: v.canonicalTag ?? null,
      customCode: v.customCode ?? null,
      keywords: v.keywords ?? null,
      allowIndexing,
      changeNote: v.changeNote ?? null,
    };
    if (isNew) {
      createMutation.mutate({ ...base, languageId: languageId ?? null, groupId: variantGroupId ?? null });
    } else {
      updateMutation.mutate({ id: id ?? '', input: { ...base, publishDate } });
    }
  };

  const currentStatus: CampaignStatus = campaign?.status ?? 'DRAFT';
  const approvalAtRisk = currentStatus === 'APPROVED' || currentStatus === 'IN_REVIEW';

  if (!isNew && isLoading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin /></div>;
  }

  return (
    <div>
      <PageHeader
        title={isNew ? t('page.newTitle') : campaign?.title ?? t('page.editTitle')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('page.listTitle'), to: ROUTES.CAMPAIGNS },
          { label: isNew ? t('page.newTitle') : t('page.editTitle') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Save size={14} />}
            htmlType="submit"
            form="campaign-edit-form"
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {isNew ? t('page.createDraft') : t('common:actions.save')}
          </Button>
        }
      />

      <Form id="campaign-edit-form" schema={schema} defaultValues={initialValues} onSubmit={onSubmit} options={{ values: initialValues }}>
        {({ watch, setValue, formState }) => {
          const titleValue = watch('title');
          const slugValue = watch('slug');
          const body = watch('body');
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!isNew && approvalAtRisk ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('common:contentWorkflow.approvalInvalidationTitle')}
                    description={t('common:contentWorkflow.approvalInvalidationDescription')}
                  />
                ) : null}
                <Card title={t('form.cards.basic')}>
                  <AntForm layout="vertical" component="div">
                    <FormField name="title" label={t('form.labels.title')} required>
                      <Input size="large" maxLength={200} showCount />
                    </FormField>
                    <FormField name="slug" label={t('form.labels.slug')} required>
                      <SlugInput
                        value={slugValue}
                        onChange={(v) => setValue('slug', v, { shouldDirty: true, shouldValidate: true })}
                        sourceValue={titleValue}
                        prefix={t('form.slugPrefix')}
                      />
                    </FormField>
                    <FormField name="excerpt" label={t('form.labels.excerpt')}>
                      <Input.TextArea rows={2} maxLength={500} showCount />
                    </FormField>
                    <FormField name="ctaLink" label={t('form.labels.ctaLink')}>
                      <Input placeholder="https://" />
                    </FormField>
                  </AntForm>
                </Card>

                <Card title={t('form.cards.body')}>
                  <RichTextEditor
                    value={body}
                    onChange={(v) => setValue('body', v, { shouldDirty: true, shouldValidate: true })}
                    maxLength={20000}
                  />
                  {formState.errors.body ? (
                    <div style={{ marginTop: 6, color: 'var(--ds-danger)', fontSize: 12 }}>
                      {formState.errors.body.message as string}
                    </div>
                  ) : null}
                </Card>

                <Card title={t('form.cards.cover')}>
                  <ImageField value={coverImageUrl} onChange={setCoverImageUrl} label={t('form.labels.cover')} maxSizeMB={5} />
                </Card>

                <Card title={t('form.cards.seo')}>
                  <SeoPanel
                    value={
                      {
                        title: watch('metaTitle') ?? '',
                        description: watch('metaDescription') ?? '',
                        slug: `kampanyalar/${slugValue}`,
                        ogImage: coverImageUrl ? { url: coverImageUrl } : null,
                      } satisfies SeoPanelValue
                    }
                    onChange={(next) => {
                      setValue('metaTitle', next.title || null, { shouldDirty: true });
                      setValue('metaDescription', next.description || null, { shouldDirty: true });
                      setCoverImageUrl(next.ogImage?.url ?? null);
                    }}
                    maxTitleLength={200}
                    maxDescriptionLength={500}
                  />
                  <AntForm layout="vertical" component="div" style={{ marginTop: 12 }}>
                    <FormField name="canonicalTag" label={t('form.labels.canonical')}>
                      <Input maxLength={500} />
                    </FormField>
                    <AntForm.Item
                      label={t('form.labels.keywords')}
                      help={t('form.keywordsHint')}
                    >
                      <KeywordsInput
                        value={watch('keywords')}
                        onChange={(v) => setValue('keywords', v, { shouldDirty: true })}
                        placeholder={t('form.placeholders.keywords')}
                      />
                    </AntForm.Item>
                    <FormField name="customCode" label={t('form.labels.customCode')}>
                      <CustomCodeField canEdit={canEditCustomCode} />
                    </FormField>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <Switch checked={allowIndexing} onChange={setAllowIndexing} />
                      <span style={{ fontSize: 13 }}>{t('form.labels.allowIndexing')}</span>
                    </div>
                  </AntForm>
                </Card>
              </div>

              <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {isNew ? (
                  <Card size="small" title={t('common:contentLanguage.label')}>
                    <LanguagePicker value={languageId} onChange={setPickedLanguageId} disabled={Boolean(variantLang)} style={{ width: '100%' }} />
                  </Card>
                ) : campaign ? (
                  <VariantPanel
                    currentLanguageId={campaign.languageId}
                    variantLanguageCodes={campaign.variantLanguageCodes}
                    contentType="campaign"
                    groupId={campaign.groupId}
                    onOpenVariant={(variantId) => void navigate(ROUTES.CAMPAIGN_EDIT.replace(':id', variantId))}
                    onAddVariant={(lang) => void navigate(`${ROUTES.CAMPAIGN_NEW}?groupId=${campaign.groupId}&lang=${lang.id}`)}
                  />
                ) : null}

                <Card size="small" title={t('form.cards.statusFlow')}>
                  {!isNew && campaign ? (
                    <div style={{ marginBottom: 12 }}>
                      <StatusBadge kind="content" status={toContentStatus(currentStatus)} size="sm" />
                      <span style={{ fontSize: 12, marginLeft: 8, color: 'var(--ds-text-secondary)' }}>{statusLabel(currentStatus)}</span>
                    </div>
                  ) : null}
                  {isNew ? (
                    <Alert type="info" showIcon message={t('statusPanel.draftAlert')} />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {currentStatus === 'DRAFT' || currentStatus === 'REJECTED' ? (
                        <Button block type="primary" loading={submitReviewMutation.isPending} disabled={mutationsBusy} onClick={() => submitReviewMutation.mutate()}>
                          {t('statusPanel.submitReview')}
                        </Button>
                      ) : null}
                      {currentStatus === 'IN_REVIEW' && isAdmin ? (
                        <>
                          <Button block type="primary" loading={approveMutation.isPending} disabled={mutationsBusy} onClick={() => approveMutation.mutate()}>
                            {t('statusPanel.approve')}
                          </Button>
                          <Button block danger loading={rejectMutation.isPending} disabled={mutationsBusy} onClick={() => void (async () => {
                            const note = await promptDialog({
                              title: t('statusPanel.rejectPrompt'),
                              defaultValue: t('statusPanel.rejectPromptDefault'),
                              okText: t('statusPanel.reject'),
                              danger: true,
                              requiredMessage: t('statusPanel.rejectReasonRequired'),
                              maxLength: 500,
                            });
                            if (note !== null) rejectMutation.mutate(note);
                          })()}>
                            {t('statusPanel.reject')}
                          </Button>
                        </>
                      ) : null}
                      {(currentStatus === 'APPROVED' || currentStatus === 'DRAFT' || currentStatus === 'ARCHIVED') && isAdmin ? (
                        <Button block type="primary" loading={publishMutation.isPending} disabled={mutationsBusy} onClick={() => publishMutation.mutate()}>
                          {t('statusPanel.publish')}
                        </Button>
                      ) : null}
                      {currentStatus === 'PUBLISHED' && isAdmin ? (
                        <Button block loading={archiveMutation.isPending} disabled={mutationsBusy} onClick={() => void (async () => {
                          if (await confirm({ title: t('statusPanel.archiveConfirmTitle'), description: t('statusPanel.archiveConfirmDescription') })) archiveMutation.mutate();
                        })()}>
                          {t('statusPanel.archive')}
                        </Button>
                      ) : null}
                      {!isAdmin && currentStatus !== 'DRAFT' && currentStatus !== 'REJECTED' ? (
                        <Alert type="warning" showIcon message={t('statusPanel.adminOnly')} />
                      ) : null}
                    </Space>
                  )}
                </Card>

                <Card size="small" title={t('form.cards.schedule')}>
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>{t('form.labels.startDate')}</div>
                      <DatePicker style={{ width: '100%' }} showTime value={startDate ? dayjs(startDate) : null} disabledDate={(d) => (endDate ? d.isAfter(dayjs(endDate)) : false)} onChange={(d) => setStartDate(d ? d.toISOString() : null)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>{t('form.labels.endDate')}</div>
                      <DatePicker style={{ width: '100%' }} showTime value={endDate ? dayjs(endDate) : null} disabledDate={(d) => (startDate ? d.isBefore(dayjs(startDate), 'day') : false)} onChange={(d) => setEndDate(d ? d.toISOString() : null)} />
                    </div>
                    {!isNew ? (
                      <div>
                        <div style={{ fontSize: 13, marginBottom: 4 }}>{t('form.labels.publishDate')}</div>
                        <DatePicker style={{ width: '100%' }} showTime value={publishDate ? dayjs(publishDate) : null} onChange={(d) => setPublishDate(d ? d.toISOString() : null)} />
                        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ds-text-tertiary)' }}>{t('form.labels.publishDateHint')}</div>
                      </div>
                    ) : null}
                  </Space>
                </Card>

                <Card size="small" title={t('form.cards.category')}>
                  <Select<string>
                    style={{ width: '100%' }}
                    allowClear
                    placeholder={t('form.labels.categoryPlaceholder')}
                    value={categoryId ?? undefined}
                    onChange={(v) => setCategoryId(v ?? null)}
                    showSearch
                    optionFilterProp="label"
                    options={(categoriesData?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
                  />
                </Card>

                {!isNew && campaign ? (
                  <Card size="small" title={t('form.cards.actions')}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Button block icon={<History size={14} />} onClick={() => setVersionsOpen(true)}>
                        {t('form.versions')}
                      </Button>
                      {canDelete ? (
                        <Button block danger icon={<Trash2 size={14} />} loading={deleteMutation.isPending} onClick={() => void (async () => {
                          if (await confirm({ title: t('deleteConfirm.title'), description: t('deleteConfirm.description'), okText: t('common:actions.delete') })) deleteMutation.mutate();
                        })()}>
                          {t('common:actions.delete')}
                        </Button>
                      ) : null}
                    </Space>
                  </Card>
                ) : null}
              </aside>
            </div>
          );
        }}
      </Form>

      {!isNew && id ? (
        <CampaignVersionsDrawer open={versionsOpen} campaignId={id} currentBody={campaign?.body ?? ''} onClose={() => setVersionsOpen(false)} />
      ) : null}
    </div>
  );
}
